import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { PageLayout } from "@/components/site/PageLayout";
import { Reveal, RevealGroup, Label, HEADING } from "@/components/site/Reveal";
import { VideoPlayer } from "@/components/site/VideoPlayer";
import { ShareLinks } from "@/components/site/ShareLinks";
import type { BlogPost } from "@/lib/blog";
import { listBlogPosts } from "@/lib/blog.functions";
import { ldScript, articleSchema, breadcrumbSchema } from "@/lib/schema";
import { absoluteUrl } from "@/lib/site";
import { canonicalPostSlug } from "@/lib/blog-canonical";

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ params }) => {
    const posts = (await listBlogPosts()) as BlogPost[];
    const post = posts.find((p) => p.slug === params.slug);
    if (!post) {
      throw notFound();
    }
    return { post, posts };
  },

  head: ({ loaderData }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Post not found · SunSmart Engineering" },
          { name: "robots", content: "noindex, nofollow" },
        ],
      };
    }
    const post = loaderData.post;
    const title = `${post.title} · SunSmart Engineering`;

    const url = absoluteUrl(`/blog/${canonicalPostSlug(post.slug)}`);
    const image = post.main_image ? absoluteUrl(post.main_image) : undefined;
    return {
      meta: [
        { title },
        { name: "description", content: post.summary },
        { property: "og:title", content: title },
        { property: "og:description", content: post.summary },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        ...(image ? [{ property: "og:image", content: image }, { name: "twitter:image", content: image }] : []),
        ...(post.video_url
          ? [
              { property: "og:video", content: post.video_url },
              { property: "og:video:type", content: "video/mp4" },
              { name: "twitter:card", content: "player" },
              { name: "twitter:player:stream", content: post.video_url },
              { name: "twitter:player:stream:content_type", content: "video/mp4" },
            ]
          : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        ldScript(
          articleSchema({
            headline: post.title,
            description: post.summary,
            url,
            image,
            datePublished: post.created_on || undefined,
            dateModified: post.last_published || post.created_on || undefined,
          }),
        ),
        ldScript(
          breadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Insights", url: "/blog" },
            { name: post.title, url },
          ]),
        ),
      ],
    };
  },
  component: BlogPostPage,
});

function formatDate(iso: string): string {
  const time = Date.parse(iso);
  if (Number.isNaN(time)) return "";
  return new Date(time).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/** Rough reading time: 220 words per minute, min 1 min. */
function readingTime(body: string): number {
  const words = body.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 220));
}

function BlogPostPage() {
  const { post, posts: allPosts } = Route.useLoaderData() as { post: BlogPost; posts: BlogPost[] };

  const idx = allPosts.findIndex((p) => p.slug === post.slug);
  const prev = idx > 0 ? allPosts[idx - 1] : undefined;
  const next = idx >= 0 && idx < allPosts.length - 1 ? allPosts[idx + 1] : undefined;

  // Related posts share the same `type`; fallback to newest others.
  const sameType = allPosts.filter((p) => p.slug !== post.slug && p.type === post.type);
  const rest = allPosts.filter((p) => p.slug !== post.slug && p.type !== post.type);
  const more = [...sameType, ...rest].slice(0, 3);

  const minutes = readingTime(post.body);

  return (
    <PageLayout
      kicker={`${post.type} · ${formatDate(post.created_on)} · ${minutes} min read`}
      title={post.title}
      intro={post.summary}
    >
      {post.video_url ? (
        <div className="border-b border-border px-6 py-12 md:px-10 md:py-16">
          <div className="mx-auto max-w-[1100px]">
            <Reveal>
              <VideoPlayer
                src={post.video_url}
                poster={post.video_poster || post.main_image}
                title={post.title}
              />
            </Reveal>
          </div>
        </div>
      ) : post.main_image ? (
        <div className="border-b border-border px-6 py-12 md:px-10 md:py-16">
          <div className="mx-auto max-w-[1100px]">
            <Reveal>
              <div className="overflow-hidden rounded-sm border border-border">
                <img src={post.main_image} alt={post.title} className="h-full w-full max-w-full object-cover" />
              </div>
            </Reveal>
          </div>
        </div>
      ) : null}

      {/* BODY */}
      <section className="border-b border-border px-6 py-16 md:px-10 md:py-24">
        <div className="mx-auto max-w-[70ch]">
          <RevealGroup stagger={80} maxStagger={160}>
            {post.highlighted_quote && (
              <blockquote className={`mb-12 border-l-2 border-sun pl-6 ${HEADING.h3} text-foreground`}>
                {post.highlighted_quote}
              </blockquote>
            )}
            <div
              className="
                [&_h1]:font-display [&_h1]:mt-12 [&_h1]:mb-4 [&_h1]:text-3xl [&_h1]:font-black [&_h1]:uppercase [&_h1]:leading-tight [&_h1]:text-foreground
                [&_h2]:font-display [&_h2]:mt-12 [&_h2]:mb-4 [&_h2]:text-2xl [&_h2]:font-black [&_h2]:uppercase [&_h2]:leading-tight [&_h2]:text-foreground
                [&_h3]:font-display [&_h3]:mt-10 [&_h3]:mb-3 [&_h3]:text-xl [&_h3]:font-bold [&_h3]:uppercase [&_h3]:leading-tight [&_h3]:text-foreground
                [&_h4]:font-display [&_h4]:mt-8 [&_h4]:mb-2 [&_h4]:text-lg [&_h4]:font-bold [&_h4]:uppercase [&_h4]:text-foreground
                [&_p]:mt-6 [&_p]:text-base [&_p]:leading-relaxed [&_p]:text-muted-foreground
                [&_a]:text-sun [&_a]:underline [&_a]:decoration-sun/40 [&_a]:underline-offset-4 hover:[&_a]:text-foreground
                [&_strong]:text-foreground [&_strong]:font-semibold
                [&_ul]:mt-6 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-6 [&_ul]:text-muted-foreground
                [&_ol]:mt-6 [&_ol]:list-decimal [&_ol]:space-y-2 [&_ol]:pl-6 [&_ol]:text-muted-foreground
                [&_li]:leading-relaxed
                [&_blockquote]:mt-8 [&_blockquote]:border-l-2 [&_blockquote]:border-sun [&_blockquote]:pl-6 [&_blockquote]:font-display [&_blockquote]:text-xl [&_blockquote]:font-bold [&_blockquote]:uppercase [&_blockquote]:leading-snug [&_blockquote]:text-foreground
                [&_img]:mt-8 [&_img]:max-w-full [&_img]:rounded-sm [&_img]:border [&_img]:border-border
                [&_hr]:my-12 [&_hr]:border-border
                [&_table]:mt-8 [&_table]:w-full [&_table]:border-collapse [&_table]:overflow-hidden [&_table]:rounded-sm [&_table]:border [&_table]:border-border [&_table]:text-sm
                [&_th]:border [&_th]:border-border [&_th]:bg-surface [&_th]:px-4 [&_th]:py-3 [&_th]:text-left [&_th]:font-display [&_th]:uppercase [&_th]:tracking-wide [&_th]:text-foreground
                [&_td]:border [&_td]:border-border [&_td]:px-4 [&_td]:py-3 [&_td]:text-muted-foreground
                [&_code]:rounded-sm [&_code]:bg-surface [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-sm [&_code]:text-sun
                [&_pre]:mt-6 [&_pre]:overflow-x-auto [&_pre]:rounded-sm [&_pre]:border [&_pre]:border-border [&_pre]:bg-surface [&_pre]:p-4
              "
            >
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  // The page title is already the H1; demote body "# " headings so each post has exactly one.
                  // `!text-3xl` keeps the original H1 size inside the [&_h2] prose styles above.
                  h1: ({ node: _node, className, ...props }) => (
                    <h2 {...props} className={`${className ?? ""} !text-3xl`.trim()} />
                  ),
                }}
              >
                {post.body}
              </ReactMarkdown>
            </div>
          </RevealGroup>

          {/* Share + prev/next nav */}
          <div className="mt-16 border-t border-border pt-8">
            <ShareLinks title={post.title} path={`/blog/${post.slug}`} />
          </div>

          {(prev || next) && (
            <nav
              aria-label="More posts"
              className="mt-8 flex flex-col gap-4 border-t border-border pt-8 sm:flex-row sm:items-start sm:justify-between"
            >
              {prev ? (
                <Link
                  to="/blog/$slug"
                  params={{ slug: prev.slug }}
                  className="group max-w-xs"
                >
                  <div className="font-mono text-[0.65rem] uppercase tracking-[0.22em] text-muted-foreground">
                    ← Previous
                  </div>
                  <div className="mt-2 font-display text-base font-bold uppercase leading-tight group-hover:text-sun">
                    {prev.title}
                  </div>
                </Link>
              ) : (
                <span />
              )}
              {next && (
                <Link
                  to="/blog/$slug"
                  params={{ slug: next.slug }}
                  className="group max-w-xs sm:text-right"
                >
                  <div className="font-mono text-[0.65rem] uppercase tracking-[0.22em] text-muted-foreground">
                    Next →
                  </div>
                  <div className="mt-2 font-display text-base font-bold uppercase leading-tight group-hover:text-sun">
                    {next.title}
                  </div>
                </Link>
              )}
            </nav>
          )}
        </div>
      </section>

      {/* MORE FROM THE BLOG */}
      {more.length > 0 && (
        <section className="border-b border-border px-6 py-24 md:px-10 md:py-32">
          <div className="mx-auto max-w-[1400px]">
            <div className="mb-12 md:mb-16">
              <RevealGroup stagger={70} maxStagger={160}>
                <Label n="+" text="Related reading" />
                <h2 className={`mt-6 ${HEADING.h2}`}>
                  Keep <span className="text-sun">reading.</span>
                </h2>
              </RevealGroup>
            </div>

            <div className="grid gap-5 md:grid-cols-3">
              {more.map((p, i) => (
                <Reveal key={p.slug} delay={Math.min(i * 60, 240)}>
                  <Link
                    to="/blog/$slug"
                    params={{ slug: p.slug }}
                    className="group block overflow-hidden rounded-sm border border-border bg-surface"
                  >
                    <div className="aspect-[4/3] overflow-hidden">
                      {p.main_image ? (
                        <img
                          src={p.main_image}
                          alt=""
                          loading="lazy"
                          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                        />
                      ) : (
                        <div className="h-full w-full bg-surface-2" />
                      )}
                    </div>
                    <div className="p-6">
                      <div className="font-mono text-[0.65rem] uppercase tracking-[0.22em] text-sun">{p.type}</div>
                      <h3 className="mt-3 font-display text-xl font-black uppercase leading-tight">{p.title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-muted-foreground line-clamp-3">{p.summary}</p>
                      <div className="mt-4 inline-flex items-center gap-2 font-mono text-[0.7rem] uppercase tracking-[0.22em] text-foreground/80 group-hover:text-sun">
                        Read more <span className="transition-transform group-hover:translate-x-1">→</span>
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="grain relative overflow-hidden bg-background px-6 py-24 md:px-10 md:py-32">
        <div
          className="pointer-events-none absolute inset-0 opacity-40"
          style={{ background: "radial-gradient(ellipse at 50% 0%, color-mix(in oklab, var(--sun) 25%, transparent) 0%, transparent 55%)" }}
        />
        <div className="relative mx-auto max-w-[1400px] text-center">
          <RevealGroup stagger={70} maxStagger={200}>
            <h2 className={`mx-auto max-w-3xl ${HEADING.h2}`}>
              Have a project in <span className="text-sun">mind?</span>
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-lg text-muted-foreground">
              Tell us what you're building and we'll help you get it engineered, sealed, and permitted.
            </p>
            <Link
              to="/contact"
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-sun px-8 py-4 text-sm font-semibold uppercase tracking-wider text-ink hover:scale-[1.03] transition-transform"
            >
              Contact us <span>→</span>
            </Link>
          </RevealGroup>
        </div>
      </section>
    </PageLayout>
  );
}
